import React from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import { motion } from "framer-motion";

const COLORS = {
  orange: "#d37d36",
  blue: "#1e4b81",
  darkBlue: "#102840",
  white: "#ffffff",
  bg: "#f8fafc",
  secondary: "#64748b",
  muted: "#4a5568",
  border: "rgba(16, 40, 64, 0.10)",
  borderSoft: "rgba(16, 40, 64, 0.07)",
};

const sectionIcons = {
  "valoramos-privacidad": <ShieldOutlinedIcon />,
  "responsable-tratamiento": <ContactMailOutlinedIcon />,
  "principios-aplicados": <GavelOutlinedIcon />,
  "obtencion-datos": <SecurityOutlinedIcon />,
  "secreto-seguridad": <LockOutlinedIcon />,
  "cambios-politica": <UpdateOutlinedIcon />,
};

const sections = [
  {
    id: "valoramos-privacidad",
    title: "Valoramos su privacidad",
    content: [
      "A través de esta política de privacidad, le informamos sobre la información que recopilamos de nuestros usuarios y cómo la utilizamos. Le instamos a revisar detenidamente estos términos antes de proporcionar sus datos en este sitio web.",
      "Hemos trabajado arduamente para establecer un entorno seguro y confiable, y queremos compartir con usted nuestros principios sobre privacidad:",
    ],
    points: [
      "Solo solicitamos información personal cuando es estrictamente necesaria para brindarle los servicios que solicita.",
      "Nunca compartimos la información personal de nuestros usuarios con terceros, a menos que sea necesario para cumplir con la ley o cuente con tu autorización expresa.",
      "Jamás utilizamos sus datos personales con fines distintos a los establecidos en esta política de privacidad.",
    ],
    footer:
      "Es importante señalar que esta Política de Privacidad puede cambiar según requisitos legislativos o autorregulaciones, por lo que le recomendamos revisarla periódicamente. Se aplicará en caso de que decida completar algún formulario en nuestro sitio web donde se solicite información personal.",
  },
  {
    id: "responsable-tratamiento",
    title: "¿Quién es el responsable del tratamiento de sus datos?",
    content: [
      `Responsable: DQTeam S.L`,
      `Correo electrónico: dqpa@dqteam.es`,
      `Dirección postal: Instituto de Tecnologías y Sistemas de Información, Camino de Moledores S/N, Ciudad Real, 13003, España`,
    ],
  },
  {
    id: "principios-aplicados",
    title: "Principios que aplicamos sobre su información personal",
    points: [
      "Principio de licitud, lealtad y transparencia: Siempre vamos a requerir su consentimiento para el tratamiento de sus datos personales.",
      "Principio de minimización de datos: Solo vamos a solicitar datos estrictamente necesarios en relación con los fines para los que los requerimos.",
      "Principio de integridad y confidencialidad: Sus datos serán tratados de tal manera que se garantice una seguridad adecuada de los datos personales y se garantice confidencialidad.",
    ],
  },
  {
    id: "obtencion-datos",
    title: "¿Cómo hemos obtenido los datos?",
    content: ["Los datos personales que tratamos en DQPa proceden de:"],
    points: [
      "Formulario de registro de usuario: Se solicitan datos como email y contraseña. Para planes premium, se solicitan nombre de la empresa, número de empleados, sector y ubicación.",
      "Formulario de contacto: Solo se solicita el email.",
    ],
  },
  {
    id: "secreto-seguridad",
    title: "Secreto y seguridad de los datos",
    content: [
      "DQPa se compromete en el uso y tratamiento de los datos personales de los usuarios, respetando su confidencialidad y a utilizarlos de acuerdo con la finalidad establecida, así como a cumplir con su obligación de guardarlos y adaptar todas las medidas necesarias para evitar alteración, pérdida, tratamiento o acceso no autorizado.",
      "Sin embargo, DQPa no puede garantizar la absoluta inexpugnabilidad de la red Internet y, por tanto, la violación de los datos mediante accesos fraudulentos por parte de terceros.",
    ],
  },
  {
    id: "cambios-politica",
    title: "Cambios en la política de privacidad",
    content: [
      "DQPa se reserva el derecho a modificar la presente política para adaptarla a novedades legislativas o jurisprudenciales, así como a prácticas de la industria. En dichos supuestos, DQPa anunciará los cambios con antelación.",
    ],
  },
];

const Politica = () => {
  return (
    <Box
      sx={{
        bgcolor: COLORS.bg,
        color: COLORS.darkBlue,
        minHeight: "100vh",
        overflowX: "hidden",
      }}
    >
      {/* HERO */}
      <Box
        sx={{
          position: "relative",
          pt: { xs: 14, md: 16 },
          pb: { xs: 6, md: 8 },
          overflow: "hidden",
          background:
            "linear-gradient(180deg, #ffffff 0%, #fbfcfd 58%, #f8fafc 100%)",
          borderBottom: `1px solid ${COLORS.borderSoft}`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(16,40,64,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(16,40,64,0.035) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            pointerEvents: "none",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            top: -120,
            right: -120,
            width: 360,
            height: 360,
            borderRadius: "50%",
            bgcolor: "rgba(211,125,54,0.12)",
            filter: "blur(80px)",
          }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Chip
                icon={<SecurityOutlinedIcon />}
                label="Privacidad y protección de datos"
                sx={{
                  mb: 2.5,
                  height: 42,
                  px: 1,
                  borderRadius: "14px",
                  bgcolor: COLORS.white,
                  border: `1px solid ${COLORS.border}`,
                  color: COLORS.darkBlue,
                  fontWeight: 900,
                  boxShadow: "0 12px 30px rgba(16,40,64,0.055)",
                  "& .MuiChip-icon": {
                    color: COLORS.orange,
                  },
                }}
              />

              <Typography
                component="h1"
                sx={{
                  color: COLORS.darkBlue,
                  fontSize: {
                    xs: "2.35rem",
                    sm: "3.1rem",
                    md: "4rem",
                  },
                  lineHeight: 1,
                  letterSpacing: "-0.055em",
                  fontWeight: 950,
                  mb: 2,
                }}
              >
                Política de{" "}
                <Box component="span" sx={{ color: COLORS.orange }}>
                  Privacidad
                </Box>
              </Typography>

              <Typography
                sx={{
                  color: COLORS.secondary,
                  fontSize: { xs: "1rem", md: "1.08rem" },
                  lineHeight: 1.7,
                }}
              >
                Última actualización: 1 de Julio de 2025
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* CONTENT */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems="flex-start"
        >
          {/* ÍNDICE LATERAL */}
          <Paper
            elevation={0}
            sx={{
              display: { xs: "none", md: "block" },
              position: "sticky",
              top: 104,
              width: 290,
              flexShrink: 0,
              p: 2,
              borderRadius: "24px",
              bgcolor: COLORS.white,
              border: `1px solid ${COLORS.borderSoft}`,
              boxShadow: "0 18px 55px rgba(16,40,64,0.055)",
            }}
          >
            <Typography
              sx={{
                color: COLORS.darkBlue,
                fontWeight: 950,
                fontSize: "0.95rem",
                mb: 1.5,
              }}
            >
              Contenido
            </Typography>

            <Stack spacing={0.4}>
              {sections.map((section) => (
                <Box
                  key={section.id}
                  component="a"
                  href={`#${section.id}`}
                  sx={{
                    display: "block",
                    px: 1.3,
                    py: 0.9,
                    borderRadius: "12px",
                    color: COLORS.secondary,
                    fontSize: "0.84rem",
                    fontWeight: 750,
                    lineHeight: 1.35,
                    textDecoration: "none",
                    transition: "all .2s ease",
                    "&:hover": {
                      bgcolor: "rgba(211,125,54,0.08)",
                      color: COLORS.orange,
                      transform: "translateX(3px)",
                    },
                  }}
                >
                  {section.title}
                </Box>
              ))}
            </Stack>
          </Paper>

          {/* DOCUMENTO */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.15 }}
            style={{ width: "100%" }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 4, md: 5 },
                borderRadius: "32px",
                border: `1px solid ${COLORS.borderSoft}`,
                boxShadow: "0 24px 70px rgba(16,40,64,0.07)",
                bgcolor: COLORS.white,
                overflow: "hidden",
              }}
            >
              {sections.map((section, index) => (
                <Box
                  key={section.id}
                  component="section"
                  id={section.id}
                  sx={{
                    scrollMarginTop: "110px",
                    "&:not(:last-child)": {
                      mb: { xs: 4, md: 5 },
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.8}
                    alignItems="flex-start"
                    sx={{ mb: 2.2 }}
                  >
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: "16px",
                        display: "grid",
                        placeItems: "center",
                        color: COLORS.orange,
                        bgcolor: "rgba(211,125,54,0.08)",
                        border: "1px solid rgba(211,125,54,0.16)",
                        flexShrink: 0,
                        "& svg": {
                          fontSize: 24,
                        },
                      }}
                    >
                      {sectionIcons[section.id] || <SecurityOutlinedIcon />}
                    </Box>

                    <Typography
                      component="h2"
                      sx={{
                        color: COLORS.darkBlue,
                        fontWeight: 950,
                        fontSize: { xs: "1.28rem", md: "1.55rem" },
                        letterSpacing: "-0.035em",
                        lineHeight: 1.2,
                        pt: 0.3,
                      }}
                    >
                      {section.title}
                    </Typography>
                  </Stack>

                  {section.content?.map((text, i) => (
                    <Typography
                      key={i}
                      paragraph
                      sx={{
                        color: COLORS.secondary,
                        fontSize: { xs: "0.98rem", md: "1.04rem" },
                        lineHeight: 1.85,
                        mb: 1.6,
                      }}
                    >
                      {text}
                    </Typography>
                  ))}

                  {section.points && (
                    <List
                      sx={{
                        py: 0,
                        mb: 2,
                        display: "grid",
                        gap: 1.2,
                      }}
                    >
                      {section.points.map((point, i) => (
                        <ListItem
                          key={i}
                          alignItems="flex-start"
                          sx={{
                            px: 1.5,
                            py: 1.25,
                            borderRadius: "16px",
                            bgcolor: COLORS.bg,
                            border: `1px solid ${COLORS.borderSoft}`,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 34,
                              color: COLORS.orange,
                              mt: "3px",
                            }}
                          >
                            <CheckCircleOutlineIcon fontSize="small" />
                          </ListItemIcon>

                          <ListItemText
                            primary={point}
                            primaryTypographyProps={{
                              sx: {
                                color: COLORS.secondary,
                                fontSize: { xs: "0.96rem", md: "1.02rem" },
                                lineHeight: 1.7,
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}

                  {section.footer && (
                    <Typography
                      paragraph
                      sx={{
                        mt: 1,
                        color: COLORS.secondary,
                        fontSize: { xs: "0.98rem", md: "1.04rem" },
                        lineHeight: 1.85,
                      }}
                    >
                      {section.footer}
                    </Typography>
                  )}

                  {index < sections.length - 1 && (
                    <Divider
                      sx={{
                        mt: { xs: 4, md: 5 },
                        borderColor: COLORS.borderSoft,
                      }}
                    />
                  )}
                </Box>
              ))}
            </Paper>
          </motion.div>
        </Stack>
      </Container>
    </Box>
  );
};

export default Politica;